import React from 'react';
import {
    Platform,
    Pressable,
    StyleSheet,
    View,
    type PressableStateCallbackType,
    type StyleProp,
    type ViewStyle,
} from 'react-native';

import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useFocusVisible } from '@/core/hooks/useFocusVisible';
import { Typography } from './Typography';
import { UAEPassIcon } from './UAEPassIcon';

/** Official UAE PASS button styles. Black on light backgrounds, white on dark, outline on white/light. */
export type UAEPassButtonVariant = 'black' | 'white' | 'outline';

export interface UAEPassButtonProps {
    title: string;
    onPress?: () => void;
    disabled?: boolean;
    variant?: UAEPassButtonVariant;
    style?: StyleProp<ViewStyle>;
    accessibilityLabel?: string;
    testID?: string;
}

/** UAE PASS guidelines: "UAE PASS" is always uppercase with a single space. */
export function formatUaePassTitle(title: string): string {
    return title.replace(/uae\s*pass/gi, 'UAE PASS');
}

const MIN_WIDTH = 140;
const MIN_HEIGHT = 30;
const HEIGHT = 48;
const LOGO_SIZE = 22;
const DISABLED_OPACITY = 0.5;
const PRESSED_OPACITY = 0.8;

export function UAEPassButton({
    title,
    onPress,
    disabled = false,
    variant = 'black',
    style,
    accessibilityLabel,
    testID,
}: Readonly<UAEPassButtonProps>) {
    const isBlack = variant === 'black';
    const fillColor = isBlack ? UI_PRIMITIVES.uaePassBlack : UI_PRIMITIVES.uaePassWhite;
    const titleColor = isBlack ? UI_PRIMITIVES.uaePassWhite : UI_PRIMITIVES.uaePassBlack;
    const formattedTitle = formatUaePassTitle(title);
    const { isFocusVisible, onFocus, onBlur } = useFocusVisible();

    return (
        <Pressable
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel ?? formattedTitle}
            accessibilityState={{ disabled }}
            disabled={disabled}
            onPress={onPress}
            onFocus={onFocus}
            onBlur={onBlur}
            testID={testID}
            style={({ pressed }: PressableStateCallbackType) => {
                return [
                    styles.base,
                    { backgroundColor: fillColor },
                    variant === 'outline' && styles.outline,
                    isFocusVisible && !disabled && styles.focused,
                    pressed && !disabled && styles.pressed,
                    disabled && styles.disabled,
                    style,
                ];
            }}
        >
            <View style={styles.content}>
                <UAEPassIcon size={LOGO_SIZE} variant={isBlack ? 'white' : 'default'} />
                <Typography
                    variant="headline"
                    color={titleColor}
                    numberOfLines={1}
                    style={styles.title}
                >
                    {formattedTitle}
                </Typography>
            </View>
        </Pressable>
    );
}

const styles = StyleSheet.create({
    base: {
        minWidth: MIN_WIDTH,
        minHeight: MIN_HEIGHT,
        height: HEIGHT,
        paddingHorizontal: 12,
        borderRadius: 4,
        alignSelf: 'stretch',
        justifyContent: 'center',
        alignItems: 'center',
        ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as unknown as ViewStyle) : null),
    },
    outline: {
        borderWidth: 1,
        borderColor: UI_PRIMITIVES.uaePassBlack,
    },
    focused: {
        outlineWidth: 2,
        outlineStyle: 'solid',
        outlineColor: UI_PRIMITIVES.uaePassBlack,
        outlineOffset: 2,
    } as ViewStyle,
    pressed: {
        opacity: PRESSED_OPACITY,
    },
    disabled: {
        opacity: DISABLED_OPACITY,
    },
    content: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
    },
    title: {
        flexShrink: 1,
    },
});

export default UAEPassButton;
